import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { X } from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';

const EditUnitDialog = ({ open, onClose, unit, onSuccess }) => {
  const { getAccessToken } = useAuth();

  const [unitNumber, setUnitNumber] = useState('');
  const [bed, setBed] = useState(0);
  const [bath, setBath] = useState(1);
  const [sqft, setSqft] = useState('');
  const [currentRent, setCurrentRent] = useState(0);
  const [status, setStatus] = useState('Vacant');
  const [leaseEnd, setLeaseEnd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && unit) {
      setUnitNumber(unit.unit || '');
      setBed(typeof unit.bed === 'number' ? unit.bed : 0);
      setBath(typeof unit.bath === 'number' ? unit.bath : 1);
      setSqft(unit.sqft != null ? String(unit.sqft) : '');
      setCurrentRent(typeof unit.currentRent === 'number' ? unit.currentRent : 0);
      setStatus(unit.status || 'Vacant');
      setLeaseEnd(unit.leaseEnd ? dayjs(unit.leaseEnd) : null);
      setError(null);
    }
  }, [open, unit]);

  const handleSave = async () => {
    if (!unit || !unit.buildingId) return;

    if (!unitNumber || bed == null || bath == null || !status) {
      setError('Please fill in all required fields.');
      return;
    }

    setSaving(true);
    setError(null);

    const unitIdToUse = unit.unitId || unit.id;

    try {
      const accessToken = getAccessToken();
      const payload = {
        unitNumber,
        bed: Number.isNaN(Number(bed)) ? 0 : Number(bed),
        bath: Number.isNaN(Number(bath)) ? 1 : Number(bath),
        sqft: sqft === '' ? null : Number(sqft),
        currentRent: Number.isNaN(Number(currentRent)) ? 0 : Number(currentRent),
        status,
        leaseEnd: leaseEnd ? leaseEnd.format('YYYY-MM-DD') : '',
      };

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/buildings/buildings/${unit.buildingId}/units/${unitIdToUse}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to update unit');
      }

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update unit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#111827',
          }}
        >
          Edit Unit
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 'auto',
            padding: '0.25rem',
            color: '#6B7280',
          }}
        >
          <X size={20} />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '1.25rem',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Unit Number *
              </Typography>
              <TextField
                fullWidth
                value={unitNumber}
                onChange={e => setUnitNumber(e.target.value)}
                placeholder="e.g., 101"
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Current Rent *
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={currentRent}
                onChange={e => setCurrentRent(e.target.value)}
                placeholder="e.g., 1450"
                InputProps={{
                  inputProps: { min: 0 },
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Bed *
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={bed}
                onChange={e => setBed(e.target.value)}
                placeholder="e.g., 2"
                InputProps={{
                  inputProps: { min: 0 },
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Bath *
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={bath}
                onChange={e => setBath(e.target.value)}
                placeholder="e.g., 1.5"
                InputProps={{
                  inputProps: { min: 0, step: 0.5 },
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Sq Ft
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={sqft}
                onChange={e => setSqft(e.target.value)}
                placeholder="e.g., 850"
                InputProps={{
                  inputProps: { min: 0 },
                }}
              />
              {/* Rent Per Sq Ft ($/SF) */}
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  Rent Per Sq Ft ($/SF):{' '}
                  {sqft && Number(sqft) > 0 && currentRent > 0
                    ? `$${(currentRent / Number(sqft)).toFixed(2)}`
                    : '-'}
                </Typography>
                {unit && unit.marketRent > 0 && sqft && Number(sqft) > 0 && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#A0AEC0' }}>
                    Mkt: ${ (unit.marketRent / Number(sqft)).toFixed(2) }
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Status *
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="unit-status-label">Status</InputLabel>
                <Select
                  labelId="unit-status-label"
                  value={status}
                  label="Status"
                  onChange={e => setStatus(e.target.value)}
                >
                  <MenuItem value="Vacant">Vacant</MenuItem>
                  <MenuItem value="Occupied">Occupied</MenuItem>
                  <MenuItem value="Notice">Notice</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: '#374151',
                }}
              >
                Lease End
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={leaseEnd}
                  onChange={newValue => setLeaseEnd(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'YYYY-MM-DD',
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '1.5rem',
          borderTop: '1px solid #E5E7EB',
          gap: '0.75rem',
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: 'none',
            color: '#374151',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            saving || !unitNumber || bed === '' || bath === '' || !status
          }
          startIcon={saving ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#39585C',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#2A3F42' },
            '&:disabled': { backgroundColor: '#9CA3AF' },
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUnitDialog;
